import { memo } from 'react';

import type {
  ImageLoadingType,
  ImageSource,
} from '@/app/components/media/image/types';
import {
  getMediaCondition,
  getSourceSrcset,
  isSvg,
} from '@/app/components/media/image/utils';

interface SourcesProps {
  sources: ImageSource[];
  loading: ImageLoadingType;
  inView?: boolean;
}

export const Sources = memo(
  ({ sources = [], loading, inView }: SourcesProps) => {
    if ((loading === 'lazy' && !inView) || sources.length === 0) {
      return null;
    }

    const hasSvg = sources.some((source) => isSvg(source.srcset));
    /**
     * Note: Check case only single svg src
     */
    if (hasSvg) {
      const uniqueSrcsets = [
        ...new Set(sources.map((source) => source.srcset)),
      ];
      if (uniqueSrcsets.length === 1) {
        return null;
      }
    }

    return sources.map((source, index) => {
      const srcSet = getSourceSrcset(source);
      const mediaCondition = getMediaCondition(source);
      const isSvgImage = isSvg(source.srcset);
      // Note: Handle case svg + jpg/png
      if (isSvgImage) {
        if (source.media?.device === 'desktop') {
          const nextItem = sources[index + 1];
          return (
            source.srcset !== nextItem?.srcset && (
              <source
                key={`source${index}`}
                srcSet={srcSet}
                media={mediaCondition}
                data-device={source.media?.device}
              />
            )
          );
        }
        if (source.media?.device === 'mobile') {
          const prevItem = sources[index - 1];
          return (
            source.srcset !== prevItem?.srcset && (
              <source
                key={`source${index}`}
                srcSet={srcSet}
                media={mediaCondition}
                data-device={source.media?.device}
              />
            )
          );
        }
      }

      return (
        <source
          key={`source${index}`}
          srcSet={srcSet}
          {...(mediaCondition && { media: mediaCondition })}
          data-device={source.media?.device}
        />
      );
    });
  }
);

Sources.displayName = 'Sources';
